/**
 * Defeasible Logic Explainer Component
 *
 * Interactive example using the classic "birds fly, penguins don't" pattern
 */

import { useState } from "react";

export default function DefeasibleExplainer() {
  const [isBird, setIsBird] = useState(true);
  const [isPenguin, setIsPenguin] = useState(false);

  const flies = isBird && !isPenguin;
  const defeated = isBird && isPenguin;

  return (
    <section className="mt-10 md:mt-16 w-full max-w-full overflow-x-hidden">
      <h2 className="text-2xl font-bold text-center mb-2">Defeasible Logic</h2>
      <p className="text-center text-base-content/60 mb-8">
        Rules that can be overridden by more specific exceptions
      </p>

      {/* Core Concepts */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="card bg-base-100 shadow-sm border border-base-200">
          <div className="card-body p-4">
            <h3 className="font-semibold text-sm text-primary">Facts</h3>
            <code className="font-mono text-xs bg-base-200 rounded px-2 py-1 block">
              (given bird)
            </code>
            <p className="text-xs text-base-content/60 mt-2">Things we know to be true</p>
          </div>
        </div>

        <div className="card bg-base-100 shadow-sm border border-base-200">
          <div className="card-body p-4">
            <h3 className="font-semibold text-sm text-secondary">Normal Rules</h3>
            <code className="font-mono text-xs bg-base-200 rounded px-2 py-1 block">
              (normally bird flies)
            </code>
            <p className="text-xs text-base-content/60 mt-2">Typical behavior, can be overridden</p>
          </div>
        </div>

        <div className="card bg-base-100 shadow-sm border border-base-200">
          <div className="card-body p-4">
            <h3 className="font-semibold text-sm text-warning">Defeaters</h3>
            <code className="font-mono text-xs bg-base-200 rounded px-2 py-1 block">
              (except penguin ¬flies)
            </code>
            <p className="text-xs text-base-content/60 mt-2">Exceptions that block conclusions</p>
          </div>
        </div>

        <div className="card bg-base-100 shadow-sm border border-base-200">
          <div className="card-body p-4">
            <h3 className="font-semibold text-sm text-success">Priorities</h3>
            <code className="font-mono text-xs bg-base-200 rounded px-2 py-1 block">
              (prefer specific general)
            </code>
            <p className="text-xs text-base-content/60 mt-2">Resolve conflicts between rules</p>
          </div>
        </div>
      </div>

      {/* Interactive Demo */}
      <div className="card bg-base-100 shadow-md border border-base-200">
        <div className="card-body p-6">
          <h3 className="font-bold text-lg mb-4">Interactive Example: Tweety</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Toggles */}
            <div className="space-y-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isBird}
                  onChange={(e) => setIsBird(e.target.checked)}
                  className="checkbox checkbox-primary"
                />
                <span>Tweety is a bird</span>
              </label>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isPenguin}
                  onChange={(e) => setIsPenguin(e.target.checked)}
                  className="checkbox checkbox-warning"
                />
                <span>Tweety is a penguin</span>
              </label>

              {/* Rules applied */}
              <div className="mt-4 space-y-2 text-sm">
                <div className={`flex items-center gap-2 ${!isBird ? "opacity-40" : ""}`}>
                  <span className="badge badge-primary badge-xs">R1</span>
                  <span className="font-mono text-xs">bird → flies</span>
                  {isBird && !isPenguin && <span className="text-success">✓</span>}
                  {isBird && isPenguin && <span className="text-error">✗ defeated</span>}
                </div>
                <div className={`flex items-center gap-2 ${!isPenguin ? "opacity-40" : ""}`}>
                  <span className="badge badge-warning badge-xs">R2</span>
                  <span className="font-mono text-xs">penguin → ¬flies</span>
                  {isPenguin && <span className="text-success">✓ (more specific)</span>}
                </div>
              </div>
            </div>

            {/* Conclusion */}
            <div className="flex flex-col items-center justify-center bg-base-200 rounded-lg p-6">
              <div className="text-sm text-base-content/60 mb-2">Conclusion:</div>
              {!isBird ? (
                <div className="text-lg text-base-content/40">No facts provided</div>
              ) : flies ? (
                <div className="flex items-center gap-2">
                  <span className="badge badge-success badge-lg">+d flies</span>
                  <span className="text-2xl">🐦</span>
                </div>
              ) : defeated ? (
                <div className="flex items-center gap-2">
                  <span className="badge badge-error badge-lg">-d flies</span>
                  <span className="text-2xl">🐧</span>
                </div>
              ) : null}

              {defeated && (
                <div className="mt-3 text-xs text-base-content/60 text-center">
                  R1 was <span className="text-error font-semibold">defeated</span> by R2
                  <br />
                  (specific beats general)
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Hence.run Application */}
      <div className="mt-8">
        <h3 className="font-bold text-lg text-center mb-4">Applied to Task Coordination</h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="card bg-primary/10 border border-primary/20">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="badge badge-primary">Ready</span>
              </div>
              <code className="font-mono text-xs bg-base-100 rounded px-2 py-1 block mb-2">
                (normally task ready)
              </code>
              <p className="text-xs text-base-content/60">
                Tasks become ready when all dependencies are satisfied
              </p>
            </div>
          </div>

          <div className="card bg-warning/10 border border-warning/30">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="badge badge-warning">Blocked</span>
              </div>
              <code className="font-mono text-xs bg-base-100 rounded px-2 py-1 block mb-2">
                (except blocker ¬ready)
              </code>
              <p className="text-xs text-base-content/60">
                Blockers defeat the "ready" conclusion until resolved
              </p>
            </div>
          </div>

          <div className="card bg-secondary/10 border border-secondary/20">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="badge badge-secondary">Assigned</span>
              </div>
              <code className="font-mono text-xs bg-base-100 rounded px-2 py-1 block mb-2">
                (prefer agent-claim first)
              </code>
              <p className="text-xs text-base-content/60">
                Priority rules determine which agent gets the task
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
